import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import {  UserPlusIcon, XMarkIcon, PlusCircleIcon, TrashIcon, MagnifyingGlassIcon, ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useNavigate ,useLocation} from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import { UserRole } from '../types'; 
import { useAuth } from '../hooks/useAuth'; 
const SCHOOL_CLASSES = [
  "الأول", "الثاني", "الثالث", "الرابع", "الخامس", "السادس",
  "السابع", "الثامن", "التاسع", "العاشر", "الحادي عشر"
];

const getCurrentAcademicYear = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const start = month >= 9 ? year : year - 1;
  return `${start}-${start + 1}`;
};

const isoToInputDate = (isoString) => {
    if (!isoString) return '';
    try {
        return new Date(isoString).toISOString().split('T')[0];
    } catch (e) { return ''; }
};

const formatDateForDisplay = (dateString) => {
    if (!dateString) return '-';
    try {
        const dateToParse = dateString.includes('T') ? dateString : `${dateString}T00:00:00`;
        return new Date(dateToParse).toLocaleDateString('ar-SY', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) { return '-'; }
}

const InfoItem = ({ label, value }) => (
    <div className="p-3 border rounded-lg bg-slate-50">
        <span className="block text-xs font-medium text-slate-500">{label}</span>
        <span className="block text-sm font-semibold text-slate-800 break-words">{value || '-'}</span>
    </div>
);


const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
};


/* ---------------- Student Modals (Redesigned) ---------------- */
const StudentConfirmationModal = ({ isOpen, onClose, data, onConfirm, isEdit }) => {
    const title = isEdit ? 'تأكيد تعديل بيانات طالب' : 'تأكيد إضافة طالب جديد';
    const fullName = `${data.FirstName} ${data.FatherName} ${data.LastName}`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <div className="space-y-6 text-right">
                <div>
                    <h3 className="text-lg font-semibold text-blue-700 border-b pb-2 mb-4">البيانات الأساسية</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <InfoItem label="الرقم التسلسلي" value={data.serialNumber} />
                        <InfoItem label="الاسم الكامل" value={fullName} />
                        <InfoItem label="اسم الأم" value={data.MotherName} />
                        <InfoItem label="الجنس" value={data.sex} />
                        <InfoItem label="تاريخ الميلاد" value={formatDateForDisplay(data.birthDate)} />
                        <InfoItem label="تاريخ الانتساب" value={formatDateForDisplay(data.HistoryOfRegistration)} />
                    </div>
                </div>
                 <div>
                    <h3 className="text-lg font-semibold text-blue-700 border-b pb-2 mb-4">بيانات التسجيل</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <InfoItem label="الصف عند التسجيل" value={data.ClassOfRegistration} />
                        <InfoItem label="المدرسة السابقة" value={data.previousSchool} />
                        <InfoItem label="وثيقة الانتساب" value={data.NumberOfDocument} />
                        <InfoItem label="صفوف الرسوب" value={data.FailClass} />
                        <InfoItem label="ملاحظات" value={data.notes} /> 
                    </div>
                </div>
                <p className="text-red-600 text-sm pt-4 text-center bg-red-50 p-3 rounded-lg">
                    الرجاء التأكد من صحة جميع البيانات قبل الحفظ النهائي.
                </p>
            </div>
            <div className="flex justify-end gap-3 pt-6 border-t mt-6">
                <Button type="button" variant="secondary" onClick={onClose}>مراجعة</Button>
                <Button type="button" onClick={onConfirm} className="bg-green-600 hover:bg-green-700">تأكيد وحفظ</Button>
            </div>
        </Modal>
    );
};

const StudentForm = ({ student, onSave, onCancel }) => {
    const getInitialState = () => ({
        serialNumber: '', FirstName: '', LastName: '', FatherName: '', GrandFatherName: '', MotherName: '',
        sex: 'ذكر', birthPlace: '', birthDate: '', Registration: '',
        Nationality: 'ع.س', Religion: 'مسلم', Language: 'ك + ف', phone:'',mobile:'',HistoryOfRegistration: '',
        previousSchool: '', NumberOfDocument: '', HistoryOfDocument: '',
        ClassOfRegistration: '', FailClass: '', notes: '',
    });
    
    const [formData, setFormData] = useState(getInitialState());
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    useEffect(() => {
        if (student) {
            setFormData({
                ...student,
                birthDate: isoToInputDate(student.birthDate),
                HistoryOfRegistration: isoToInputDate(student.HistoryOfRegistration),
            });
        } else {
            setFormData(getInitialState());
        }
    }, [student]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleReview = (e) => {
        e.preventDefault();
        if (!formData.serialNumber || !formData.FirstName || !formData.LastName || !formData.FatherName || !formData.ClassOfRegistration) {
            toast.error('الرجاء تعبئة الحقول الأساسية المطلوبة: الرقم التسلسلي، الاسم، اللقب، اسم الأب، وصف الانتساب.');
            return;
        }
        setIsConfirmationOpen(true);
    };

    const handleConfirmSave = () => {
        const dataToSave = { ...formData };
        onSave(dataToSave);
        setIsConfirmationOpen(false);
    }

    return (
        <>
            <form onSubmit={handleReview} className="space-y-8">
                 <div className="p-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-800">البيانات الشخصية</h3>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 px-4">
                    <Input label="الرقم التسلسلي" id="serialNumber" value={formData.serialNumber} onChange={handleChange} required />
                    <Input label="اسم التلميذ" id="FirstName" value={formData.FirstName} onChange={handleChange} required />
                    <Input label="اللقب" id="LastName" value={formData.LastName} onChange={handleChange} required />
                    <Input label="اسم الأب" id="FatherName" value={formData.FatherName} onChange={handleChange} required />
                    <Input label="اسم الجد" id="GrandFatherName" value={formData.GrandFatherName} onChange={handleChange} />
                    <Input label="اسم الأم" id="MotherName" value={formData.MotherName} onChange={handleChange} />
                     <div>
                        <label htmlFor="sex" className="block text-sm font-medium text-gray-700 mb-2">الجنس</label>
                        <select id="sex" name="sex" value={formData.sex} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                            <option value="ذكر">ذكر</option>
                            <option value="أنثى">أنثى</option>
                        </select>
                    </div>
                    <Input label="مكان الولادة" id="birthPlace" value={formData.birthPlace} onChange={handleChange} />
                    <Input label="تاريخ الميلاد" id="birthDate" type="date" value={formData.birthDate} onChange={handleChange} required />
                    <Input label="محل ورقم القيد" id="Registration" value={formData.Registration} onChange={handleChange} />
                    <Input label="الجنسية" id="Nationality" value={formData.Nationality} onChange={handleChange} />
                    <div>

                        <label htmlFor="Religion" className="block text-sm font-medium text-gray-700 mb-2">الديانة</label>
                        <select id="Religion" name="Religion" value={formData.Religion} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                            <option value="مسلم">مسلم</option>
                            <option value="مسيحي">مسيحي</option>
                        </select>
                    </div>                    <Input label="اللغة الأجنبية" id="Language" value={formData.Language} onChange={handleChange} />
                    <Input label="رقم الموبايل" id="mobile" value={formData.mobile} onChange={handleChange} />
                    <Input label="رقم الأرضي" id="phone" value={formData.phone} onChange={handleChange} />

                </div>
                <div className="p-4 border-y">
                    <h3 className="text-lg font-semibold text-gray-800">بيانات التسجيل</h3>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 px-4">
                    <Input label="تاريخ الانتساب للمدرسة" id="HistoryOfRegistration" type="date" value={formData.HistoryOfRegistration} onChange={handleChange} required />
                    <Input label="المدرسة التي انتقل منها" id="previousSchool" value={formData.previousSchool} onChange={handleChange} />
                    <Input label="رقم وثيقته عند الانتساب" id="NumberOfDocument" value={formData.NumberOfDocument} onChange={handleChange} />
                    <Input label="تاريخ وثيقته عند الانتساب" id="HistoryOfDocument"  value={formData.HistoryOfDocument} onChange={handleChange} />
                    <Input label="الصف الذي انتسب اليه" id="ClassOfRegistration" value={formData.ClassOfRegistration} onChange={handleChange} required />
                    <Input label="صفوف الرسوب السابقة" id="FailClass" value={formData.FailClass} onChange={handleChange} />
                    <Input label="ملاحظات" id="notes" value={formData.notes} onChange={handleChange} className="md:col-span-3"/>
                </div>
                <div className="flex justify-end gap-3 pt-6 border-t px-4">
                    <Button type="button" variant="secondary" onClick={onCancel} className="flex items-center gap-1"><XMarkIcon className="h-4 w-4" /> إلغاء</Button>
                    <Button type="submit" className="flex items-center gap-1">مراجعة البيانات وحفظ</Button>
                </div>
            </form>
            <StudentConfirmationModal isOpen={isConfirmationOpen} onClose={() => setIsConfirmationOpen(false)} data={formData} onConfirm={handleConfirmSave} isEdit={!!student} />
        </>
    );
};

/* ---------------- Transfer Modals (Redesigned) ---------------- */
const TransferConfirmationModal = ({ type, isOpen, onClose, data, onConfirm }) => {
    const isTransferIn = type === 'in';
    const title = isTransferIn ? 'تأكيد إضافة تحويل دخول' : 'تأكيد إضافة تحويل خروج';
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <div className="space-y-6 text-right">
                <div>
                    <h3 className="text-lg font-semibold text-blue-700 border-b pb-2 mb-4">بيانات الطالب</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                         <InfoItem label="الرقم التسلسلي" value={data.selectedStudent?.serialNumber} />
                         <InfoItem label="الاسم الكامل" value={`${data.selectedStudent?.FirstName} ${data.selectedStudent?.FatherName} ${data.selectedStudent?.LastName}`} />
                    </div>
                </div>
                 <div>
                    <h3 className="text-lg font-semibold text-blue-700 border-b pb-2 mb-4">بيانات النقل</h3>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InfoItem label="العام الدراسي" value={data.academicYear} />
                        {isTransferIn ? (
                            <>
                                <InfoItem label="المحافظة المنقول منها" value={data.provinceIn} />
                                <InfoItem label="تاريخ إرسال وثيقة الإعلام" value={data.notificationDate} />
                                <InfoItem label="عنوان المدرسة المنقول منها" value={data.previousSchoolAddress} />
                            </>
                        ) : (
                            <>
                                <InfoItem label="الصف الذي انتقل منه" value={data.previousClass} />
                                <InfoItem label="رقم وتاريخ وثيقة اللامانع" value={data.clearanceDoc} />
                                <InfoItem label="تاريخ النقل" value={data.transferDate} />
                                <InfoItem label="اسم المدرسة المنقول إليها" value={data.newSchoolName} />
                                <InfoItem label="المحافظة المنقول إليها" value={data.provinceOut} />
                                <InfoItem label="رقم وتاريخ وثيقة الإعلام بالدوام" value={data.notificationDoc} />
                                <InfoItem label="عنوان المدرسة المنقول إليها" value={data.newSchoolAddress} />
                            </>
                        )}
                    </div>
                </div>
                <p className="text-red-600 text-sm pt-4 text-center bg-red-50 p-3 rounded-lg">يرجى التأكد من صحة جميع البيانات قبل الإضافة النهائية.</p>
            </div>
            <div className="flex justify-end gap-2 pt-6 border-t mt-6">
                <Button type="button" variant="secondary" onClick={onClose}>مراجعة</Button>
                <Button type="button" onClick={onConfirm} className="bg-green-600 hover:bg-green-700">تأكيد وحفظ</Button>
            </div>
        </Modal>
    );
};

const AddTransferModal = ({ type = 'in', isOpen, onClose, onSuccess, preselectedStudent = null }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(preselectedStudent);
    const [academicYear, setAcademicYear] = useState(getCurrentAcademicYear());
    const [provinceIn, setProvinceIn] = useState('');
    const [notificationDate, setNotificationDate] = useState('');
    const [previousSchoolAddress, setPreviousSchoolAddress] = useState('');
    const [previousClass, setPreviousClass] = useState('');
    const [clearanceDoc, setClearanceDoc] = useState('');
    const [transferDate, setTransferDate] = useState('');
    const [newSchoolName, setNewSchoolName] = useState('');
    const [provinceOut, setProvinceOut] = useState('');
    const [notificationDoc, setNotificationDoc] = useState('');
    const [newSchoolAddress, setNewSchoolAddress] = useState('');
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    useEffect(() => { setSelectedStudent(preselectedStudent || null); }, [preselectedStudent]);
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery(''); setSearchResults([]); setSelectedStudent(preselectedStudent || null);
            setAcademicYear(getCurrentAcademicYear());
            setProvinceIn(''); setNotificationDate(''); setPreviousSchoolAddress('');
            setPreviousClass(''); setClearanceDoc(''); setTransferDate(''); setNewSchoolName('');
            setProvinceOut(''); setNotificationDoc(''); setNewSchoolAddress('');
            setIsConfirmationOpen(false);
        }
    }, [isOpen, preselectedStudent]);

    const handleSearch = async (q) => {
        setSearchQuery(q);
        if (!q || q.trim().length < 1) { setSearchResults([]); return; }
        try {
            const { data } = await api.searchStudents(q);
            setSearchResults(data);
        } catch (err) { console.error('Search failed', err); }
    };

    const openConfirmation = (e) => {
        e.preventDefault();
        if (!selectedStudent?._id) { toast.error('اختر طالباً أولاً'); return; }
        if (type === 'in' && (!provinceIn || !notificationDate || !previousSchoolAddress)) { toast.error('املأ جميع حقول تحويل الدخول'); return; }
        if (type === 'out' && (!previousClass || !clearanceDoc || !transferDate || !newSchoolName || !provinceOut || !notificationDoc || !newSchoolAddress)) { toast.error('املأ جميع حقول تحويل الخروج'); return; }
        setIsConfirmationOpen(true);
    };

    const handleSubmit = async () => {
        if (!selectedStudent?._id) return;
        try {
            if (type === 'in') {
                await api.addTransferIn({ studentId: selectedStudent._id, academicYear, province: provinceIn, notificationDate, previousSchoolAddress });
                toast.success('تم إضافة تحويل دخول بنجاح');
            } else {
                await api.addTransferOut({ studentId: selectedStudent._id, academicYear, previousClass, clearanceDoc, transferDate, newSchoolName, province: provinceOut, notificationDoc, newSchoolAddress });
                toast.success('تم إضافة تحويل خروج بنجاح');
            }
            onSuccess?.();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'فشل في حفظ النقل');
        } finally { setIsConfirmationOpen(false); }
    };

    const confirmationData = { selectedStudent, academicYear, provinceIn, notificationDate, previousSchoolAddress, previousClass, clearanceDoc, transferDate, newSchoolName, provinceOut, notificationDoc, newSchoolAddress };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title={type === 'in' ? 'إضافة تحويل إلى المدرسة' : 'إضافة تحويل من المدرسة'}>
                <form onSubmit={openConfirmation} className="space-y-4">
                    {!preselectedStudent && (
                        <div className='relative'>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ابحث عن طالب (الاسم أو الرقم)</label>
                            <Input value={searchQuery} onChange={(e) => handleSearch(e.target.value)} placeholder="ابحث..." />
                            {searchResults.length > 0 && (
                                <ul className="absolute z-10 w-full border rounded-md max-h-48 overflow-auto mt-2 bg-white shadow-lg">
                                    {searchResults.map(s => (<li key={s._id} className={`p-2 cursor-pointer hover:bg-gray-100 ${selectedStudent?._id === s._id ? 'bg-blue-50' : ''}`} onClick={() => { setSelectedStudent(s); setSearchResults([]); setSearchQuery(''); }}>{s.serialNumber} — {s.FirstName} {s.FatherName} {s.LastName}</li>))}
                                </ul>
                            )}
                        </div>
                    )}
                    {selectedStudent && (<div className="p-3 bg-blue-50 rounded-lg"><div className="text-sm text-blue-800 font-semibold">المحدد: {selectedStudent.serialNumber} — {selectedStudent.FirstName} {selectedStudent.FatherName} {selectedStudent.LastName}</div></div>)}
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">العام الدراسي</label><Input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} required /></div>
                    {type === 'in' ? (<><Input label="المحافظة التابعة لها المدرسة المنقول منها" value={provinceIn} onChange={(e) => setProvinceIn(e.target.value)} required /><Input label="تاريخ إرسال وثيقة الاعلام بالدوام" type="date" value={notificationDate} onChange={(e) => setNotificationDate(e.target.value)} required /><Input label="عنوان المدرسة المنقول منها + رقم الهاتف" value={previousSchoolAddress} onChange={(e) => setPreviousSchoolAddress(e.target.value)} required /></>) : (<><Input label="الصف الذي انتقل منه" value={previousClass} onChange={(e) => setPreviousClass(e.target.value)} required /><Input label="رقم وتاريخ وثيقة اللامانع" value={clearanceDoc} onChange={(e) => setClearanceDoc(e.target.value)} required /><Input label="تاريخ النقل" type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} required /><Input label="اسم المدرسة المنقول إليها" value={newSchoolName} onChange={(e) => setNewSchoolName(e.target.value)} required /><Input label="المحافظة التابعة لها" value={provinceOut} onChange={(e) => setProvinceOut(e.target.value)} required /><Input label="رقم وتاريخ وثيقة الاعلام بالدوام" value={notificationDoc} onChange={(e) => setNotificationDoc(e.target.value)} required /><Input label="عنوان المدرسة المنقول إليها + رقم الهاتف" value={newSchoolAddress} onChange={(e) => setNewSchoolAddress(e.target.value)} required /></>)}
                    <div className="flex justify-end gap-3 pt-4 border-t"><Button type="button" variant="secondary" onClick={onClose}>إلغاء</Button><Button type="submit">مراجعة وإضافة</Button></div>
                </form>
            </Modal>
            <TransferConfirmationModal type={type} isOpen={isConfirmationOpen} onClose={() => setIsConfirmationOpen(false)} data={confirmationData} onConfirm={handleSubmit} />
        </>
    );
};

/* ---------------- Out Of School Modals (Redesigned) ---------------- */
const AddOutOfSchoolModal = ({ isOpen, onClose, onSuccess }) => {
    const getInitialState = () => ({
        studyingResults: [{ className: SCHOOL_CLASSES[0], academicYear: getCurrentAcademicYear(), result: '' }],
        classOfOut: SCHOOL_CLASSES[0],
        historyOfOut: '',
        reasons: '',
        documentOfOuting: { type: '', number: '', history: '' }
    });
    const [formData, setFormData] = useState(getInitialState());
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setFormData(getInitialState());
            setSelectedStudent(null);
            setSearchQuery('');
            setSearchResults([]);
            setIsConfirmationOpen(false);
        }
    }, [isOpen]);

    const handleSearch = async (q) => {
        setSearchQuery(q);
        if (!q.trim()) { setSearchResults([]); return; }
        const { data } = await api.searchStudents(q);
        setSearchResults(data);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDocumentChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, documentOfOuting: { ...prev.documentOfOuting, [name]: value } }));
    };

    const handleResultChange = (index, e) => {
        const { name, value } = e.target;
        const updatedResults = [...formData.studyingResults];
        updatedResults[index][name] = value;
        setFormData(prev => ({ ...prev, studyingResults: updatedResults }));
    };

    const addResult = () => {
        setFormData(prev => ({ ...prev, studyingResults: [...prev.studyingResults, { className: SCHOOL_CLASSES[0], academicYear: getCurrentAcademicYear(), result: '' }] }));
    };

    const removeResult = (index) => {
        const updatedResults = formData.studyingResults.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, studyingResults: updatedResults }));
    };

    const handleReview = (e) => {
        e.preventDefault();
        if (!selectedStudent) { toast.error('الرجاء اختيار طالب أولاً.'); return; }
        if (!formData.classOfOut || !formData.historyOfOut || !formData.reasons) { toast.error('الرجاء تعبئة حقول الخروج المطلوبة.'); return; }
        if (formData.studyingResults.some(r => !r.className || !r.academicYear || !r.result)) { toast.error('الرجاء تعبئة جميع حقول نتائج الدراسة.'); return; }
        setIsConfirmationOpen(true);
    };
    
    const handleConfirmSave = async () => {
      const payload = {
        studentId: selectedStudent.serialNumber,
        ...formData,
        documentOfOuting: {
          ...formData.documentOfOuting,
          history: formData.documentOfOuting.history || null
        }
      };
      try {
        await api.addOutOfSchoolRecord(payload);
        toast.success("تم تسجيل خروج الطالب بنجاح.");
        onSuccess();
        onClose();
      } catch (err) {
        toast.error(err.response?.data?.message || 'فشل تسجيل الخروج');
      } finally {
        setIsConfirmationOpen(false);
      }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title="تسجيل خروج طالب من المدرسة">
                <form onSubmit={handleReview} className="space-y-6">
                     <div className='relative'>
                        <label className="block text-sm font-medium text-slate-700 mb-1">ابحث عن طالب</label>
                        <Input value={searchQuery} onChange={(e) => handleSearch(e.target.value)} placeholder="ابحث بالاسم أو الرقم..." />
                         {searchResults.length > 0 && (
                            <ul className="absolute z-10 w-full border rounded-md max-h-48 overflow-auto mt-2 bg-white shadow-lg">
                                {searchResults.map(s => (<li key={s._id} className="p-2 cursor-pointer hover:bg-gray-100" onClick={() => { setSelectedStudent(s); setSearchResults([]); setSearchQuery(''); }}>{s.serialNumber} — {s.FirstName} {s.FatherName} {s.LastName}</li>))}
                            </ul>
                        )}
                        {selectedStudent && (<div className="mt-2 p-3 bg-blue-50 rounded-lg"><div className="text-sm text-blue-800 font-semibold">المحدد: {selectedStudent.serialNumber} — {selectedStudent.FirstName} {selectedStudent.FatherName} {selectedStudent.LastName}</div></div>)}
                    </div>

                    <div className="border-t pt-5">
                        <h3 className="text-lg font-medium text-slate-800 mb-3">نتائج الدراسة</h3>
                        <div className="space-y-3">
                        {formData.studyingResults.map((result, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center p-3 bg-slate-50 rounded-lg">
                                 <select name="className" value={result.className} onChange={(e) => handleResultChange(index, e)} className="w-full px-3 py-2 border  rounded-lg font-medium shadow-sm bg-white focus:ring-2 focus:ring-blue-500">
                                    {SCHOOL_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <Input name="academicYear" placeholder="العام الدراسي" value={result.academicYear} onChange={(e) => handleResultChange(index, e)} required />
                                <Input name="result" placeholder="النتيجة" value={result.result} onChange={(e) => handleResultChange(index, e)} required />
                                <Button type="button" variant="danger" onClick={() => removeResult(index)} className="flex items-center justify-center gap-1 h-10"><TrashIcon className="h-4 w-4" /> <span>حذف</span></Button>
                            </div>
                        ))}
                        </div>
                        <Button type="button" variant="ghost" onClick={addResult} className="flex items-center gap-1 text-blue-600 mt-3"><PlusCircleIcon className="h-5 w-5" /> إضافة نتيجة</Button>
                    </div>

                     <div className="border-t pt-5">
                        <h3 className="text-lg font-medium text-slate-800 mb-3">تفاصيل الخروج</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-lg font-medium text-slate-700 mb-1">الصف الذي تركه</label>
                                <select name="classOfOut" value={formData.classOfOut} onChange={handleChange} className="w-full px-3 py-2 border  rounded-lg shadow-sm bg-white focus:ring-2 focus:ring-blue-500">
                                    {SCHOOL_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <Input label="تاريخ تركه" name="historyOfOut" type="date" value={formData.historyOfOut} onChange={handleChange} required />
                            <Input label="الأسباب" name="reasons" value={formData.reasons} onChange={handleChange} required className="md:col-span-2"/>
                        </div>
                    </div>
                     <div className="border-t pt-5">
                        <h3 className="text-lg font-medium text-slate-800 mb-3">وثيقته عند الخروج (اختياري)</h3>
                         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                           <Input label="نوعها" name="type" value={formData.documentOfOuting.type} onChange={handleDocumentChange} />
                           <Input label="رقمها" name="number" value={formData.documentOfOuting.number} onChange={handleDocumentChange} />
                           <Input label="تاريخها" name="history" type="date" value={formData.documentOfOuting.history} onChange={handleDocumentChange} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-6 border-t"><Button type="button" variant="secondary" onClick={onClose}>إلغاء</Button><Button type="submit">مراجعة وحفظ</Button></div>
                </form>
            </Modal>
            
            <OutOfSchoolConfirmationModal isOpen={isConfirmationOpen} onClose={() => setIsConfirmationOpen(false)} data={formData} student={selectedStudent} onConfirm={handleConfirmSave} />
        </>
    );
};

const OutOfSchoolConfirmationModal = ({ isOpen, onClose, data, student, onConfirm }) => (
    <Modal isOpen={isOpen} onClose={onClose} title="تأكيد تسجيل خروج طالب">
        <div className="space-y-6 text-right">
            <div>
                 <h3 className="text-lg font-semibold text-blue-700 border-b pb-2 mb-4">بيانات الطالب</h3>
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoItem label="الرقم التسلسلي" value={student?.serialNumber} />
                    <InfoItem label="الاسم الكامل" value={student ? `${student.FirstName} ${student.FatherName} ${student.LastName}` : ''} />
                </div>
            </div>
            
            <div>
                <h3 className="text-lg font-semibold text-slate-700 border-b pb-2 mb-4">نتائج الدراسة</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                    {data.studyingResults.map((r, i) => (
                        <div key={i} className="grid grid-cols-3 gap-2 p-2 bg-gray-50 rounded">
                            <InfoItem label="الصف" value={r.className} />
                            <InfoItem label="العام الدراسي" value={r.academicYear} />
                            <InfoItem label="النتيجة" value={r.result} />
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <h3 className="text-lg font-semibold text-slate-700 border-b pb-2 mb-4">تفاصيل الخروج</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <InfoItem label="الصف الذي تركه" value={data.classOfOut} />
                    <InfoItem label="تاريخ تركه" value={formatDateForDisplay(data.historyOfOut)} />
                    <InfoItem label="الأسباب" value={data.reasons} />
                </div>
            </div>

            <div>
                <h3 className="text-lg font-semibold text-slate-700 border-b pb-2 mb-4">وثيقته عند الخروج</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <InfoItem label="نوعها" value={data.documentOfOuting.type} />
                    <InfoItem label="رقمها" value={data.documentOfOuting.number} />
                    <InfoItem label="تاريخها" value={formatDateForDisplay(data.documentOfOuting.history)} />
                </div>
            </div>

            <p className="text-red-600 text-sm pt-4 text-center bg-red-50 p-3 rounded-lg">يرجى التأكد من صحة جميع البيانات قبل الحفظ النهائي.</p>
        </div>
        <div className="flex justify-end gap-2 pt-6 border-t mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>مراجعة</Button>
            <Button type="button" onClick={onConfirm} className="bg-green-600 hover:bg-green-700">تأكيد وحفظ</Button>
        </div>
    </Modal>
);

const StudentsPage = () => {
  const auth = useAuth();
  const role = auth?.user?.role || auth?.role || (Array.isArray(auth?.user?.roles) ? auth.user.roles[0] : null);

  const isAccountant = role === UserRole.ACCOUNTANT;
  //const isOwnerOrSecretary = role === UserRole.OWNER || role === UserRole.SECRETARY;

  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [activeTab, setActiveTab] = useState('students');

  const [transfersIn, setTransfersIn] = useState([]);
  const [transfersInPage, setTransfersInPage] = useState(1);
  const [transfersInPages, setTransfersInPages] = useState(1);
  const [transfersInTotal, setTransfersInTotal] = useState(0);
  const [transfersInLoading, setTransfersInLoading] = useState(false);
  const [transferInAcademicYear, setTransferInAcademicYear] = useState(getCurrentAcademicYear());

  const [transfersOut, setTransfersOut] = useState([]);
  const [transfersOutPage, setTransfersOutPage] = useState(1);
  const [transfersOutPages, setTransfersOutPages] = useState(1);
  const [transfersOutTotal, setTransfersOutTotal] = useState(0);
  const [transfersOutLoading, setTransfersOutLoading] = useState(false);
  const [transferOutAcademicYear, setTransferOutAcademicYear] = useState(getCurrentAcademicYear());

  const [outOfSchoolRecords, setOutOfSchoolRecords] = useState([]);
  const [outOfSchoolPage, setOutOfSchoolPage] = useState(1);
  const [outOfSchoolPages, setOutOfSchoolPages] = useState(1);
  const [outOfSchoolTotal, setOutOfSchoolTotal] = useState(0);
  const [outOfSchoolLoading, setOutOfSchoolLoading] = useState(false);

  const [isAddTransferOpen, setIsAddTransferOpen] = useState(false);
  const [addTransferType, setAddTransferType] = useState('in');
  const [isAddOutOfSchoolOpen, setIsAddOutOfSchoolOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const navState = location.state;
    if (navState?.autoOpenModal) {
      openModal();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate]);

  // force students tab for accountant
  useEffect(() => {
    if (isAccountant && activeTab !== 'students') setActiveTab('students');
  }, [isAccountant, activeTab]);

  const fetchStudents = async (pageNum = 1) => {
    setLoading(true);
    try {
      const { data } = await api.getStudents(pageNum, 10);
      setStudents(data.students || []); setTotal(data.total || 0);
      setPages(data.pages || 1); setPage(data.page || pageNum);
    } catch (error) { toast.error('فشل في جلب قائمة الطلاب'); }
    finally { setLoading(false); }
  };

  const fetchTransfersIn = async (pageNum = 1, year = transferInAcademicYear) => {
    setTransfersInLoading(true);
    try {
      const { data } = await api.getTransfersIn(pageNum, 10, year);
      setTransfersIn(data.transfers || []); setTransfersInTotal(data.total || 0);
      setTransfersInPages(data.pages || 1); setTransfersInPage(data.page || pageNum);
    } catch (err) { toast.error('فشل في جلب تحويلات الدخول'); }
    finally { setTransfersInLoading(false); }
  };

  const fetchTransfersOut = async (pageNum = 1, year = transferOutAcademicYear) => {
    setTransfersOutLoading(true);
    try {
      const { data } = await api.getTransfersOut(pageNum, 10, year);
      setTransfersOut(data.transfers || []); setTransfersOutTotal(data.total || 0);
      setTransfersOutPages(data.pages || 1); setTransfersOutPage(data.page || pageNum);
    } catch (err) { toast.error('فشل في جلب تحويلات الخروج'); }
    finally { setTransfersOutLoading(false); }
  };

  const fetchOutOfSchoolRecords = async (pageNum = 1) => {
    setOutOfSchoolLoading(true);
    try {
      const { data } = await api.getOutOfSchoolRecords(pageNum, 10);
      setOutOfSchoolRecords(data.records || []); setOutOfSchoolTotal(data.total || 0);
      setOutOfSchoolPages(data.pages || 1); setOutOfSchoolPage(data.page || pageNum);
    } catch (err) { toast.error('فشل في جلب سجلات الخروج من المدرسة'); }
    finally { setOutOfSchoolLoading(false); }
  };

  useEffect(() => {
    // Only fetch data for tabs the user is allowed to see.
    if (activeTab === 'students') fetchStudents(page);
    else if (!isAccountant && activeTab === 'transfersIn') fetchTransfersIn(transfersInPage, transferInAcademicYear);
    else if (!isAccountant && activeTab === 'transfersOut') fetchTransfersOut(transfersOutPage, transferOutAcademicYear);
    else if (!isAccountant && activeTab === 'outOfSchool') fetchOutOfSchoolRecords(outOfSchoolPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, transfersInPage, transfersOutPage, outOfSchoolPage]);

  useEffect(() => {
    if (activeTab === 'transfersIn') { setTransfersInPage(1); fetchTransfersIn(1, transferInAcademicYear); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferInAcademicYear]);

  useEffect(() => {
    if (activeTab === 'transfersOut') { setTransfersOutPage(1); fetchTransfersOut(1, transferOutAcademicYear); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferOutAcademicYear]);

  const handleInteractiveSearch = async (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    try {
      const { data } = await api.searchStudents(q);
      setSearchResults(data);
    } catch (err) { setSearchResults([]); }
  };

  // <-- UPDATED: allow navigation from search results for ALL roles (including accountants) -->
  const handleSearchResultClick = (serialNumber) => {
    setSearchQuery('');
    setSearchResults([]);
    navigate(`/students/${serialNumber}`);
  };

  const handleConfirmSave = async (studentData) => {
    try {
      if (editingStudent) {
        await api.updateStudent(editingStudent._id, studentData);
      } else {
        await api.addStudent(studentData);
      }
      fetchStudents(page);
      closeModal();
      toast.success('تم الحفظ بنجاح');
    } catch (error) { toast.error(error.response?.data?.message || 'فشل حفظ الطالب'); }
  };

  const openModal = (student = null) => {
    if (isAccountant) return; // accountants cannot open modals
    setEditingStudent(student); setIsModalOpen(true);
  };
  const closeModal = () => { setEditingStudent(null); setIsModalOpen(false); };
  const handleRowClick = (serialNumber) => {
    navigate(`/students/${serialNumber}`);
  };
  const openAddTransfer = (type) => { if (isAccountant) return; setAddTransferType(type); setIsAddTransferOpen(true); };
  const closeAddTransfer = () => setIsAddTransferOpen(false);

  const onTransferAdded = () => {
    if (activeTab === 'transfersIn') fetchTransfersIn(transfersInPage, transferInAcademicYear);
    if (activeTab === 'transfersOut') fetchTransfersOut(transfersOutPage, transferOutAcademicYear);
  };

  const onOutOfSchoolAdded = () => fetchOutOfSchoolRecords(outOfSchoolPage);

  const allTabs = [
    { id: 'students', label: 'السجل العام' },
    { id: 'transfersIn', label: 'المنقولين إلى المدرسة' },
    { id: 'transfersOut', label: 'المنقولين من المدرسة' },
    { id: 'outOfSchool', label: 'الخروج من المدرسة' },
  ];
  const visibleTabs = isAccountant ? allTabs.filter(t => t.id === 'students') : allTabs;

  const tableClasses = "min-w-full text-slate-900 text-lg";
  const headClasses = "bg-slate-70 text-slate-900 uppercase tracking-wider";
  const headerCellClasses = "py-3 px-4 text-right font-bold border-b";

  const getActionButtons = () => {
    if (isAccountant) return null;
    const buttons = {
      'students': { onClick: () => openModal(), text: 'إضافة طالب جديد', color: 'bg-blue-600 hover:bg-blue-700' },
      'transfersIn': { onClick: () => openAddTransfer('in'), text: 'إضافة تحويل دخول', color: 'bg-green-600 hover:bg-green-700' },
      'transfersOut': { onClick: () => openAddTransfer('out'), text: 'إضافة تحويل خروج', color: 'bg-red-600 hover:bg-red-700' },
      'outOfSchool': { onClick: () => setIsAddOutOfSchoolOpen(true), text: 'إضافة خروج طالب', color: 'bg-orange-600 hover:bg-orange-700' }
    };
    const btn = buttons[activeTab];
    if (!btn) return null;
    return <Button onClick={btn.onClick} className={`flex items-center gap-2 text-white rounded-lg px-4 py-2 shadow-md ${btn.color}`}><UserPlusIcon className="h-5 w-5" /> {btn.text}</Button>;
  };

  return (
    <div dir="rtl" className="bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen p-4 sm:p-6 lg:p-8 font-sans">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 space-y-1"
      >
        <h1 className="text-4xl font-extrabold text-slate-1000 tracking-tight">إدارة شؤون الطلاب</h1>
        <p className="text-slate-700 text-lg">عرض، إضافة، وتعديل سجلات الطلاب والتحويلات.</p>
      </motion.header>

      <div className="mb-6">
        <nav className="flex space-x-2 space-x-reverse p-1.5 bg-slate-200/70 rounded-xl overflow-x-auto" aria-label="Tabs">
          {visibleTabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`relative whitespace-nowrap flex-1 px-3 py-2.5 text-md font-semibold rounded-lg transition-colors ${activeTab === tab.id ? "text-slate-900" : "text-slate-700 hover:bg-white/60"}`}
            >
              {activeTab === tab.id && (
                <motion.div layoutId="active-student-tab" className="absolute inset-0 bg-white shadow-md rounded-lg"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
              <span className="relative z-10">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <Card className="shadow-xl rounded-2xl p-6 bg-white/80 backdrop-blur-sm border border-slate-100">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="w-full md:w-72 relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-800 absolute top-1/2 -translate-y-1/2 left-3 pointer-events-none" />
            <Input id="interactive-search" placeholder="بحث بالاسم أو الرقم التسلسلي  ...  " value={searchQuery} onChange={(e) => handleInteractiveSearch(e.target.value)} className="rounded-lg shadow-lg !pr-10 " />
            {searchResults.length > 0 && (
              <ul className="absolute z-10 w-full border border-gray-300 rounded-lg max-h-80 overflow-auto mt-1 bg-white shadow-lg">
                {searchResults.map(s => (
                  <li
                    key={s._id}
                    // UPDATED: clickable for all roles (accountant included)
                    className={`p-3 cursor-pointer hover:bg-blue-50 transition-colors text-lg `}
                    onClick={() => handleSearchResultClick(s.serialNumber)}
                  >
                    {s.serialNumber} — {s.FirstName} {s.FatherName} {s.LastName}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {getActionButtons()}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'students' && (
              <>
                {loading ? (<div className="flex justify-center items-center py-10"><p className="text-slate-700 animate-pulse">جاري التحميل...</p></div>) : (
                  <>
                    <div className="overflow-x-auto rounded-xl border  shadow-sm">
                      <table className={tableClasses}>
                        <thead className={headClasses}>
                          <tr>
                            <th className={headerCellClasses}>الرقم</th>
                            <th className={headerCellClasses}>الاسم </th>
                            <th className={headerCellClasses}>اسم الأب</th>
                            <th className={headerCellClasses}>الكنية </th>
                            <th className={headerCellClasses}>اسم الأم</th>
                            <th className={headerCellClasses}>الصف</th>
                            <th className={headerCellClasses}>تاريخ الميلاد</th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-slate-100'>
                          {students.length > 0 ? (students.map((student) => (
                              <tr key={student._id} className={`transition-colors duration-200 cursor-pointer bg-white hover:bg-slate-50`}title="اضغط للإنتقال إلى صفحة الطالب" onClick={() => handleRowClick(student.serialNumber)}>
                              <td className="py-3 px-4 text-right font-medium  text-slate-1000 ">{student.serialNumber}</td>
                              <td className="py-3 px-4 text-right font-medium  text-slate-1000">{student.FirstName || '-'}</td>
                              <td className="py-3 px-4 text-right font-medium  text-slate-1000">{student.FatherName || '-'}</td>
                              <td className="py-3 px-4 text-right font-medium  text-slate-1000">{student.LastName || '-'}</td>
                              <td className="py-3 px-4 text-right font-medium  text-slate-1000">{student.MotherName || '-'}</td>
                              <td className="py-3 px-4 text-right font-medium  text-slate-1000"><span className='bg-blue-100 text-blue-800 text-xs text-lg px-2 py-1 rounded-full'>{student.className || 'غير محدد'}</span></td>
                              <td className="py-3 px-4 text-right font-medium text-slate-1000">{formatDateForDisplay(student.birthDate)}</td>
                            </tr>
                          ))) : (<tr><td colSpan="7" className="text-center py-8 text-gray-500 italic">لا يوجد طلاب.</td></tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Pagination page={page} setPage={setPage} totalPages={pages} totalItems={total} />
                  </>
                )}
              </>
            )}

            {!isAccountant && activeTab === 'transfersIn' && (
              <>
                <div className="flex justify-start items-center mb-4">
                  <div className="flex items-center gap-3">
                    <label htmlFor="transfer-in-year-filter" className="text-sm font-medium text-gray-700">تصفية حسب العام الدراسي:</label>
                    <Input id="transfer-in-year-filter" value={transferInAcademicYear} onChange={(e) => setTransferInAcademicYear(e.target.value)} placeholder="مثال: 2024-2025" className="w-40" />
                  </div>
                </div>
                {transfersInLoading ? (<div className="flex justify-center items-center py-10"><p className="text-gray-500 animate-pulse">جاري التحميل...</p></div>) : (
                  <>
                    <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                      <table className={tableClasses}>
                        <thead className={headClasses}>
                          <tr>
                            <th className={headerCellClasses}>الرقم</th>
                            <th className={headerCellClasses}>الاسم الكامل</th>
                            <th className={headerCellClasses}>العام الدراسي</th>
                            <th className={headerCellClasses}>تاريخ الإعلام</th>
                            <th className={headerCellClasses}>المحافظة</th>
                            <th className={headerCellClasses}>عنوان المدرسة السابقة</th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-slate-100'>
                          {transfersIn.length > 0 ? transfersIn.map((t) => (
                            <tr key={t._id} className="bg-white hover:bg-slate-50 transition-colors">
                              <td className="py-3 px-4 font-medium  text-slate-1000 font-mono">{t.student?.serialNumber || '-'}</td>
                              <td className="py-3 px-4 font-medium text-slate-1000">{t.student ? `${t.student.FirstName} ${t.student.FatherName} ${t.student.LastName}` : '-'}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{t.academicYear}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{formatDateForDisplay(t.transferIn.notificationDate)}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{t.transferIn?.province || '-'}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{t.transferIn?.previousSchoolAddress || '-'}</td>
                            </tr>
                          )) : (<tr><td colSpan="6" className="text-center py-8 text-gray-500 italic">لا توجد تحويلات دخول.</td></tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Pagination page={transfersInPage} setPage={setTransfersInPage} totalPages={transfersInPages} totalItems={transfersInTotal} />
                  </>
                )}
              </>
            )}

            {!isAccountant && activeTab === 'transfersOut' && (
              <>
                <div className="flex justify-start items-center mb-4">
                  <div className="flex items-center gap-3">
                    <label htmlFor="transfer-out-year-filter" className="text-sm font-medium text-gray-700">تصفية حسب العام الدراسي:</label>
                    <Input id="transfer-out-year-filter" value={transferOutAcademicYear} onChange={(e) => setTransferOutAcademicYear(e.target.value)} placeholder="مثال: 2024-2025" className="w-40" />
                  </div>
                </div>
                {transfersOutLoading ? (<div className="flex justify-center items-center py-10"><p className="text-gray-500 animate-pulse">جاري التحميل...</p></div>) : (
                  <>
                    <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                      <table className={tableClasses}>
                        <thead className={headClasses}>
                          <tr>
                            <th className={headerCellClasses}>الرقم</th>
                            <th className={headerCellClasses}>الاسم الكامل</th>
                            <th className={headerCellClasses}>العام</th>
                            <th className={headerCellClasses}>الصف</th>
                            <th className={headerCellClasses}>تاريخ النقل</th>
                            <th className={headerCellClasses}>المدرسة الجديدة</th>
                            <th className={headerCellClasses}>المحافظة</th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-slate-100'>
                          {transfersOut.length > 0 ? transfersOut.map((t) => (
                            <tr key={t._id} className="bg-white hover:bg-slate-50 transition-colors">
                              <td className="py-3 px-4 font-mono font-medium  text-slate-1000">{t.student?.serialNumber || '-'}</td>
                              <td className="py-3 px-4 font-medium text-slate-1000">{t.student ? `${t.student.FirstName} ${t.student.FatherName} ${t.student.LastName}` : '-'}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{t.academicYear}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{t.transferOut?.previousClass || '-'}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{formatDateForDisplay(t.transferOut.transferDate)}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{t.transferOut?.newSchoolName || '-'}</td>
                              <td className="py-3 px-4 font-medium  text-slate-1000">{t.transferOut?.province || '-'}</td>
                            </tr>
                          )) : (<tr><td colSpan="7" className="text-center py-8 text-gray-500 italic">لا توجد تحويلات خروج.</td></tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Pagination page={transfersOutPage} setPage={setTransfersOutPage} totalPages={transfersOutPages} totalItems={transfersOutTotal} />
                  </>
                )}
              </>
            )}

            {!isAccountant && activeTab === 'outOfSchool' && (
              <>
                {outOfSchoolLoading ? (<div className="flex justify-center items-center py-10"><p className="text-gray-500 animate-pulse">جاري التحميل...</p></div>) : (
                  outOfSchoolRecords.length > 0 ? (
                    <div className="space-y-4">
                      {outOfSchoolRecords.map((r) => (
                        <OutOfSchoolRecordCard key={r._id} record={r} />
                      ))}
                      <Pagination page={outOfSchoolPage} setPage={setOutOfSchoolPage} totalPages={outOfSchoolPages} totalItems={outOfSchoolTotal} />
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500 italic">لا توجد سجلات خروج مضافة حالياً.</div>
                  )
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </Card>

      {/* Only render modals/actions for non-accountants */}
      {!isAccountant && (
        <>
          <Modal isOpen={isModalOpen} onClose={closeModal} title={editingStudent ? '✏️ تعديل بيانات طالب' : '➕ إضافة طالب جديد'}>
            {/* StudentForm should be imported/available */}
            <StudentForm student={editingStudent} onSave={handleConfirmSave} onCancel={closeModal} />
          </Modal>
          <AddTransferModal type={addTransferType} isOpen={isAddTransferOpen} onClose={closeAddTransfer} onSuccess={onTransferAdded} />
          <AddOutOfSchoolModal isOpen={isAddOutOfSchoolOpen} onClose={() => setIsAddOutOfSchoolOpen(false)} onSuccess={onOutOfSchoolAdded} />
        </>
      )}

    </div>
  );
};

const Pagination = ({ page, setPage, totalPages, totalItems }) => {
  if (totalItems === 0) return null;
  return (
    <div className="flex justify-between items-center mt-6 px-2 text-sm">
      <p className="text-slate-600">
        عرض {Math.min((page - 1) * 10 + 1, totalItems)} - {Math.min(page * 10, totalItems)} من {totalItems}
      </p>
      <div className="flex gap-2 items-center">
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-full disabled:text-slate-300 disabled:cursor-not-allowed text-slate-600 hover:bg-slate-100 transition-colors">
          <ArrowRightIcon className="h-4 w-4" />
        </button>
        <span className="px-3 py-1 bg-slate-100 text-slate-700 font-medium rounded-lg">
          {page} / {totalPages}
        </span>
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-full disabled:text-slate-300 disabled:cursor-not-allowed text-slate-600 hover:bg-slate-100 transition-colors">
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const OutOfSchoolRecordCard = ({ record }) => {
  return (
    <motion.div variants={itemVariants} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="p-4 bg-slate-50 border-b flex justify-between items-center">
        <div className="font-bold text-slate-1000">{record.student ? `${record.student.FirstName} ${record.student.FatherName} ${record.student.LastName}` : 'طالب غير محدد'}</div>
        <div className="font-semibold text-slate-800 ">الرقم: {record.student?.serialNumber || '-'}</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200">
        <div className="p-4 bg-white">
          <h4 className="font-bold text-slate-1000 border-b pb-2 mb-3">نتائج الدراسة</h4>
          <div className="max-h-40 overflow-auto pr-2 space-y-2">
            {record.studyingResults.map((res, idx) => (
              <div key={idx} className="grid grid-cols-3 gap-2 text-md p-2 bg-slate-50 rounded-md">
                <div><span className="font-bold text-slate-900">الصف:</span> {res.className}</div>
                <div><span className="font-bold text-slate-900">العام:</span> {res.academicYear}</div>
                <div><span className="font-bold text-slate-900">النتيجة:</span> {res.result}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="p-4 bg-white space-y-3">
          <h4 className="font-bold text-slate-1000 border-b pb-2 mb-3">تفاصيل الخروج</h4>
          <div className="text-md space-y-2">
            <p><span className="font-bold text-slate-900">الصف الذي تركه:</span> {record.classOfOut}</p>
            <p><span className="font-bold text-slate-900">تاريخ تركه:</span> {formatDateForDisplay(record.historyOfOut)}</p>
            <p><span className="font-bold text-slate-900">الأسباب:</span> {record.reasons}</p>
          </div>
          {record.documentOfOuting && (record.documentOfOuting.type || record.documentOfOuting.number || record.documentOfOuting.history) && (
            <div className="pt-3 border-t text-md space-y-1">
              <h5 className="font-bold text-slate-1000">وثيقته عند الخروج:</h5>
              <p className="pl-2"><span className="font-bold text-slate-900">نوعها:</span> {record.documentOfOuting.type || '-'}</p>
              <p className="pl-2"><span className="font-bold text-slate-900">رقمها:</span> {record.documentOfOuting.number || '-'}</p>
              <p className="pl-2"><span className="font-bold text-slate-900">تاريخها:</span> {formatDateForDisplay(record.documentOfOuting.history)}</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default StudentsPage;
